import { BodyStats_Input } from "@/clients/models/BodyStats_Input";
import { QueryKeys } from "@/constants/query-keys";
import { useGetBodyStats } from "@/hooks/requests/body-status/use-get-body-stats";
import { useUpdateBodyStats } from "@/hooks/requests/body-status/use-update-body-stats";
import { useToast } from "@/hooks/ui/use-toast";
import { useStepRouteInfo } from "@/hooks/use-step-route-info";
import { cn } from "@/utils/cn";
import { IonContent, IonPage, useIonViewDidEnter } from "@ionic/react";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { OnboardingNextFab } from "../../components/onboarding-next-fab";
import { OnboardingStepShell } from "../../components/onboarding-step-shell";

type Unit = "metric" | "imperial";

const DEFAULT_CM = 170;
const LB_PER_KG = 2.2046226218;

const kgToLb = (kg: number) => Math.round(kg * LB_PER_KG * 10) / 10;
const lbToKg = (lb: number) => Math.round((lb / LB_PER_KG) * 10) / 10;

interface SegmentedControlProps {
  value: Unit;
  onChange: (value: Unit) => void;
}

const SegmentedControl = ({ value, onChange }: SegmentedControlProps) => (
  <div className="flex h-11 w-[322px] items-center gap-2 rounded-[32px] bg-[#eff1f5] p-1">
    {(["metric", "imperial"] as const).map((unit) => (
      <button
        key={unit}
        type="button"
        onClick={() => onChange(unit)}
        className={cn(
          "flex h-full flex-1 items-center justify-center rounded-[37px] text-base font-medium text-black",
          value === unit && "bg-white shadow-[4px_4px_24px_0_rgba(215,214,214,0.15)]",
        )}
      >
        {unit === "metric" ? "kg" : "lbs"}
      </button>
    ))}
  </div>
);

const OnboardingWeightPage = () => {
  const { navigateToNextStep, preloadNextRoute } = useStepRouteInfo();
  const { bodyStats } = useGetBodyStats();
  const queryClient = useQueryClient();
  const { updateBodyStatsAsync, isUpdating } = useUpdateBodyStats();
  const { showError } = useToast();
  const hydratedRef = useRef(false);

  const [unit, setUnit] = useState<Unit>("metric");
  const [input, setInput] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useIonViewDidEnter(() => {
    inputRef.current?.focus();
  });

  useEffect(() => {
    if (hydratedRef.current) return;
    const existing = Number(bodyStats?.currentWeight);
    if (existing > 0) {
      setInput(String(existing));
      hydratedRef.current = true;
    }
  }, [bodyStats?.currentWeight]);

  const handleUnitChange = (next: Unit) => {
    const parsed = parseFloat(input);
    if (!isNaN(parsed) && parsed > 0) {
      if (unit === "metric" && next === "imperial") {
        setInput(String(kgToLb(parsed)));
      } else if (unit === "imperial" && next === "metric") {
        setInput(String(lbToKg(parsed)));
      }
    }
    setUnit(next);
  };

  const parsedValue = parseFloat(input);
  const canSubmit = !isNaN(parsedValue) && parsedValue > 0 && !isUpdating;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    const kg = unit === "imperial" ? lbToKg(parsedValue) : parsedValue;
    const payload: BodyStats_Input = {
      height: Number(bodyStats?.height) || DEFAULT_CM,
      heightUnit: "cm",
      startingWeight: kg,
      startingWeightUnit: "kg",
      currentWeight: kg,
      currentWeightUnit: "kg",
      targetWeight: kg,
      targetWeightUnit: "kg",
    };
    try {
      await Promise.all([preloadNextRoute(), updateBodyStatsAsync(payload)]);
      await queryClient.invalidateQueries({ queryKey: [QueryKeys.BODY_STATS] });
      navigateToNextStep();
    } catch (error) {
      showError(error instanceof Error ? error.message : "An unexpected error occurred");
    }
  };

  return (
    <IonPage>
      <IonContent fullscreen>
        <OnboardingStepShell
          icon="/assets/images/onboarding-icons/weight.svg"
          title="Weight"
          subtitle="This helps me calculate things more accurately"
        >
          <div className="flex w-full flex-col items-center gap-4">
            <SegmentedControl value={unit} onChange={handleUnitChange} />
            <input
              ref={inputRef}
              type="text"
              inputMode="decimal"
              value={input}
              onChange={(e) => setInput(e.target.value.replace(/[^0-9.]/g, ""))}
              placeholder={unit === "metric" ? "Add weight (e.g. 52.5)" : "Add weight (e.g. 115)"}
              autoFocus
              className="h-[50px] w-full max-w-[321px] rounded-[40px] border border-purple-700 bg-white px-5 text-sm font-medium text-gray-950 outline-none placeholder:font-medium placeholder:text-[#5f6d8a]"
            />
          </div>
          <OnboardingNextFab visible={canSubmit} onClick={handleSubmit} disabled={isUpdating} />
        </OnboardingStepShell>
      </IonContent>
    </IonPage>
  );
};

export default OnboardingWeightPage;
